import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'

import type { TypeParams } from './type-params'

/** TODO: Explain */
export class Api implements ec.models.Api<TypeParams> {
  /** @inheritdoc */
  public readonly list = ec.models.Api.list.using.everything<TypeParams>(
    (context, request) => {
      if (!context?.content.query) {
        return rxjs.of([])
      }

      const { content, zoneId, tenantId } = context
      TypeAsserter.assertIsNotNullish(content.query)

      const query = ents.core.display.Table.Query.Modification.apply(
        content.query,
        {
          ...request,
          search: request.filter?.searchTerm,
        },
      )

      const listQuery = dms.common.gql.queries.data
        .execute({
          query: JSON.stringify(query),
          tenantId,
          zoneId,
        })
        .pipe(
          rxjs.map(result =>
            // NOTE: We have to cast this here because dmsQuery returns unknown
            (result as ents.core.display.Table.Row[]).map(
              (row, index) =>
                ({
                  id: { value: index },
                  ...row,
                }) satisfies ents.core.display.Table.Row & {
                  id: ents.core.display.Table.Field
                },
            ),
          ),
        )

      return context.content.totalRow
        ? rxjs
            .forkJoin([getTotalRow(context, request), listQuery])
            .pipe(
              rxjs.map(([totalRow, rows]) =>
                rows.length ? [...rows, totalRow] : [],
              ),
            )
        : listQuery
    },
  )

  constructor(
    private readonly _schema$: rxjs.Observable<ents.core.dms.models.Schema>,
  ) {}

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] =
    context => {
      // If context is not available, return no attributes
      if (!context) {
        return rxjs.of([])
      }

      const {
        content: {
          // default to empty array if columns is not available
          columns = [],
        },
      } = context

      return this._schema$.pipe(
        rxjs.map(schema =>
          columns.map(column => {
            const { id, isSortable = true } = column

            const columnMetadata =
              ents.core.display.Field.Metadata.Source.getMetadata(
                column,
                schema,
              )
            const type = columnMetadata?.type
              ? ents.core.dms.data.Value.Type.toCommonValueType(
                  columnMetadata?.type,
                  schema,
                )
              : undefined
            return {
              getValueFrom: row => row.fields?.[id]?.value,
              id,
              isSortable,
              // Fall back to column id name on metadata is not available
              name: columnMetadata?.name ?? id,
              valueInfo: { type },
            }
          }),
        ),
      )
    }
}

export namespace Api {
  /**
   * Api with count method implemented to support a case when we pass count to
   * our table query.
   *
   * The reason we need this Api is because we do not want to call count method
   * when our table query doesn't have count.
   */
  export class WithCount extends Api {
    /** @inheritdoc */
    public readonly count: ec.models.Api<TypeParams>['count'] = (
      context,
      request,
    ) => {
      // If context is not available, return no count
      if (!context) {
        return rxjs.of(0)
      }

      const {
        tenantId,
        zoneId,
        content: { count },
      } = context

      const query = ents.core.display.Table.Query.Modification.apply(count, {
        ...request,
        search: request?.filter?.searchTerm,
      })

      return (
        dms.common.gql.queries.data
          .execute({
            query: JSON.stringify(query),
            tenantId: tenantId,
            zoneId: zoneId,
          })
          // NOTE: Count expects number, but dms query returns us unknown
          // because of the query flexibility, so we assert that result is a
          // number
          .pipe(
            rxjs.map(result =>
              TypeAsserter.validateButJustWarn(
                result,
                TypeAsserter.assertIsNumber,
              ),
            ),
          )
      )
    }
  }
}

/**
 * Queries for the total row for the table widget
 *
 * NOTE: - This is currently just a workaround for displaying a total row on the
 * table widget. We will eventually have to refactor the UI table component to
 * support this kind of thing
 */
function getTotalRow(
  context: TypeParams['context'],
  request: ec.models.QueryRequest<TypeParams>,
): rxjs.Observable<TypeParams['entity']> {
  // NOTE: We expect context to be defined but our type supports undefined, so
  // we just assert it's not nullish
  TypeAsserter.assertIsNotNullish(context)

  const { content, ...queryContext } = context

  // NOTE: We expect that the total row is defined at this point, so we just
  // assert it's not nullish
  TypeAsserter.assertIsNotNullish(content.totalRow)

  return dms.common.gql.queries.data
    .execute({
      ...queryContext,
      query: JSON.stringify(
        ents.core.display.Table.Query.Modification.apply(content.totalRow, {
          ...request,
          search: request?.filter?.searchTerm,
        }),
      ),
    })
    .pipe(
      rxjs.map(row => ({
        // NOTE: We have to cast this here because totalRow query returns
        // unknown
        ...(row as ents.core.display.Table.Row),
        // FIXME: - We should probably extract this to a const somewhere
        id: { value: 'totalRow' },
      })),
    )
}
